import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import de from "./locales/de";
import en from "./locales/en";
import es from "./locales/es";
import fr from "./locales/fr";

const DICTS = { de, en, es, fr };
export const LANGS = ["de", "en", "es", "fr"];
const LOCALE_TAG = { de: "de-DE", en: "en-US", es: "es-ES", fr: "fr-FR" };
const STORAGE_KEY = "mw_lang";

function detectDefault() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && LANGS.includes(saved)) return saved;
    const nav = (navigator.language || "de").slice(0, 2).toLowerCase();
    if (LANGS.includes(nav)) return nav;
  } catch { /* ignore */ }
  return "de";
}

// Supports {name} and {{name}} placeholders.
function substitute(template, vars) {
  if (!vars) return template;
  return template.replace(/\{\{?(\w+)\}?\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

const Ctx = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(detectDefault);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      if (typeof document !== "undefined") document.documentElement.lang = lang;
    } catch { /* ignore */ }
  }, [lang]);

  const t = useCallback((key, vars) => {
    const dict = DICTS[lang] || DICTS.de;
    const val = dict[key] ?? DICTS.de[key] ?? key;
    return substitute(val, vars);
  }, [lang]);

  const setLang = useCallback((l) => { if (LANGS.includes(l)) setLangState(l); }, []);

  const value = useMemo(() => ({
    lang, setLang, t, langs: LANGS, locale: LOCALE_TAG[lang],
  }), [lang, setLang, t]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export function useT() {
  return useI18n().t;
}
